import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { QK } from '../../lib/queryKeys';
import { fetchInvoiceStandardItems } from '../../lib/queryFunctions';
import SidebarLayout from '../../components/layout/SidebarLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { ArrowLeft, Plus, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import { useConfirm } from '../../components/common/ConfirmDialog';
import { Checkbox } from '../../components/ui/checkbox';

const BLANK_FORM = { items: '', description: '', price: '', is_proceeds: false, is_rebillable: false, is_direct_allocation: false };

// ── Shared Entry Type box — matches AddLineItemDialog exactly ─────────────────
const EntryTypeBox = ({ form, setForm }) => (
  <div className="rounded-lg border bg-muted/20 p-3 space-y-3">
    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Entry Type</p>
    <div className="flex items-start gap-3">
      <div
        className="flex items-start gap-2 flex-1 cursor-pointer"
        onClick={() => setForm(f => ({ ...f, is_proceeds: false }))}
      >
        <input
          type="radio" id="type-expense" name="entry-type"
          checked={!form.is_proceeds}
          onChange={() => setForm(f => ({ ...f, is_proceeds: false }))}
          className="w-4 h-4 mt-0.5"
        />
        <div>
          <Label htmlFor="type-expense" className="text-sm font-medium cursor-pointer">Expense</Label>
          <p className="text-xs text-muted-foreground">Charged to client (e.g. training, vet fees)</p>
        </div>
      </div>
      <div
        className="flex items-start gap-2 flex-1 cursor-pointer"
        onClick={() => setForm(f => ({ ...f, is_proceeds: true, is_rebillable: false }))}
      >
        <input
          type="radio" id="type-proceeds" name="entry-type"
          checked={!!form.is_proceeds}
          onChange={() => setForm(f => ({ ...f, is_proceeds: true, is_rebillable: false }))}
          className="w-4 h-4 mt-0.5"
        />
        <div>
          <Label htmlFor="type-proceeds" className="text-sm font-medium cursor-pointer">Other Proceeds</Label>
          <p className="text-xs text-muted-foreground">Paid out to owner (e.g. race winnings, prize money)</p>
        </div>
      </div>
    </div>

    {/* Rebillable — only for Expense entries */}
    {!form.is_proceeds && (
      <div className="flex items-center gap-2 pt-1">
        <Checkbox
          id="rebillable-chk"
          checked={!!form.is_rebillable}
          onCheckedChange={(v) => setForm(f => ({ ...f, is_rebillable: v }))}
        />
        <Label htmlFor="rebillable-chk" className="text-sm">Rebillable Expense (markup applies)</Label>
      </div>
    )}

    {/* Direct Allocation — always shown (template may be pre-flagged for DA invoices) */}
    <div className="flex items-center gap-2 pt-1">
      <Checkbox
        id="da-chk"
        checked={!!form.is_direct_allocation}
        onCheckedChange={(v) => setForm(f => ({ ...f, is_direct_allocation: v }))}
      />
      <div>
        <Label htmlFor="da-chk" className="text-sm">Direct Allocation</Label>
        <p className="text-xs text-muted-foreground">(If Multiple Owners, applies solely to this Account)</p>
      </div>
    </div>
  </div>
);

const StandardItemsPage = () => {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: items = [], isLoading: loading } = useQuery({
    queryKey: QK.invoiceStandardItems(),
    queryFn: fetchInvoiceStandardItems(),
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: QK.invoiceStandardItems() });

  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(BLANK_FORM);
  const [saving, setSaving] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState(BLANK_FORM);
  const confirm = useConfirm();

  const handleCreate = async () => {
    if (!form.items || !form.price) { toast.error('Item name and price are required'); return; }
    setSaving(true);
    try {
      await api.post('/invoices/standard-items', { ...form, price: parseFloat(form.price) || 0 });
      toast.success('Item saved');
      setCreateOpen(false);
      setForm(BLANK_FORM);
      invalidate();
    } catch { toast.error('Failed to create'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!await confirm({ title: 'Delete this saved item?', destructive: true })) return;
    try {
      await api.delete(`/invoices/standard-items/${id}`);
      toast.success('Item deleted');
      invalidate();
    } catch { toast.error('Failed to delete'); }
  };

  const openEdit = (item) => {
    setEditItem(item);
    setEditForm({
      items:               item.items,
      description:         item.description || '',
      price:               item.price,
      is_proceeds:         item.is_proceeds         || false,
      is_rebillable:       item.is_rebillable       || false,
      is_direct_allocation: item.is_direct_allocation || false,
    });
  };

  const handleUpdate = async () => {
    if (!editForm.items || !editForm.price) { toast.error('Item name and price are required'); return; }
    setSaving(true);
    try {
      await api.put(`/invoices/standard-items/${editItem.id}`, { ...editForm, price: parseFloat(editForm.price) || 0 });
      toast.success('Item updated');
      setEditItem(null);
      invalidate();
    } catch { toast.error('Failed to update'); }
    setSaving(false);
  };

  return (
    <SidebarLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/invoices')}>
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <div>
              <h1 className="font-serif text-2xl font-semibold">Saved Invoice Items</h1>
              <p className="text-sm text-muted-foreground">Reusable line items for invoices</p>
            </div>
          </div>
          <Button onClick={() => setCreateOpen(true)} className="bg-primary hover:bg-primary/90 text-white">
            <Plus className="w-4 h-4 mr-2" /> New Item
          </Button>
        </div>

        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>
            ) : items.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground">No saved items yet. Click "New Item" to create one.</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Item</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="text-right">Price</TableHead>
                    <TableHead>Entry Type</TableHead>
                    <TableHead className="w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map(item => (
                    <TableRow key={item.id} className="cursor-pointer hover:bg-muted/50" onClick={() => openEdit(item)}>
                      <TableCell className="font-medium">{item.items}</TableCell>
                      <TableCell className="text-muted-foreground">{item.description || '-'}</TableCell>
                      <TableCell className="text-right">${parseFloat(item.price).toLocaleString()}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 flex-wrap">
                          <Badge variant="outline" className="text-xs">
                            {item.is_proceeds ? 'Proceeds' : 'Expense'}
                          </Badge>
                          {item.is_rebillable && (
                            <Badge variant="secondary" className="text-xs">Rebillable</Badge>
                          )}
                          {item.is_direct_allocation && (
                            <Badge className="text-xs bg-amber-50 text-amber-700 border border-amber-200">Direct Alloc.</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost" size="sm" className="h-7 w-7 p-0 text-muted-foreground hover:text-red-600"
                          onClick={async (e) => { e.stopPropagation(); handleDelete(item.id); }}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={(v) => { if (!v) { setCreateOpen(false); setForm(BLANK_FORM); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>New Saved Invoice Item</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Item Name *</Label>
              <Input
                value={form.items}
                onChange={(e) => setForm(f => ({ ...f, items: e.target.value }))}
                placeholder="e.g., Monthly Board"
                data-testid="create-item-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={form.description}
                onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Optional description"
              />
            </div>
            <div className="space-y-2">
              <Label>Price *</Label>
              <Input
                type="number" value={form.price}
                onChange={(e) => setForm(f => ({ ...f, price: e.target.value }))}
                placeholder="0.00"
                data-testid="create-item-price"
              />
            </div>
            <EntryTypeBox form={form} setForm={setForm} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCreateOpen(false); setForm(BLANK_FORM); }}>Cancel</Button>
            <Button
              onClick={handleCreate} disabled={saving}
              className="bg-primary hover:bg-primary/90 text-white"
              data-testid="create-item-submit"
            >
              {saving ? 'Saving...' : 'Save Item'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editItem} onOpenChange={(v) => { if (!v) setEditItem(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Edit Invoice Item</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Item Name *</Label>
              <Input
                value={editForm.items}
                onChange={(e) => setEditForm(f => ({ ...f, items: e.target.value }))}
                data-testid="edit-item-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={editForm.description}
                onChange={(e) => setEditForm(f => ({ ...f, description: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Price *</Label>
              <Input
                type="number" value={editForm.price}
                onChange={(e) => setEditForm(f => ({ ...f, price: e.target.value }))}
                data-testid="edit-item-price"
              />
            </div>
            <EntryTypeBox form={editForm} setForm={setEditForm} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditItem(null)}>Cancel</Button>
            <Button
              onClick={handleUpdate} disabled={saving}
              className="bg-primary hover:bg-primary/90 text-white"
              data-testid="edit-item-submit"
            >
              {saving ? 'Saving...' : 'Update Item'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SidebarLayout>
  );
};

export default StandardItemsPage;
